import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@telegram-apps/telegram-ui';
import { BottomNav } from '../components/design/BottomNav';
import { brand, fontFamily, tg } from '../theme/tokens';
import {
  listCatalog,
  listGenerations,
  type Model,
  type Generation,
} from '../lib/api';

type SubTab = 'catalog' | 'kisses';

/**
 * Library tab — Catalog (browse models) + My Kisses (history).
 * Favorites is intentionally deferred (needs new DB table for user×model link).
 */
export function LibraryPage() {
  const [tab, setTab] = useState<SubTab>('catalog');

  // Tab roots don't own MainButton — previous page's cleanup hid it. Same for
  // BackButton: nothing to do here, it's already hidden after wizard pages
  // unmounted theirs.

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 100, fontFamily }}>
      <div style={{ padding: '6px 16px 14px' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: tg.text, letterSpacing: -0.4 }}>
          Library
        </div>
      </div>

      <Tabs active={tab} onChange={setTab} />

      {tab === 'catalog' ? <CatalogList /> : <MyKissesList />}

      <BottomNav />
    </div>
  );
}

function Tabs({ active, onChange }: { active: SubTab; onChange: (t: SubTab) => void }) {
  const items: { id: SubTab; label: string }[] = [
    { id: 'catalog', label: 'Catalog' },
    { id: 'kisses', label: 'My Kisses' },
  ];
  return (
    <div style={{ padding: '0 16px 12px' }}>
      <div
        style={{
          display: 'flex',
          background: tg.bg3,
          borderRadius: 12,
          padding: 3,
        }}
      >
        {items.map((it) => {
          const on = it.id === active;
          return (
            <button
              key={it.id}
              onClick={() => onChange(it.id)}
              style={{
                appearance: 'none',
                border: 'none',
                flex: 1,
                padding: '8px 0',
                borderRadius: 9,
                textAlign: 'center',
                background: on ? tg.bg2 : 'transparent',
                fontSize: 13,
                fontWeight: 600,
                color: on ? tg.text : tg.textHint,
                boxShadow: on ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                fontFamily,
              }}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function CatalogList() {
  const [models, setModels] = useState<Model[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listCatalog()
      .then(setModels)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <EmptyState title="Couldn't load catalog" subtitle={error} />;
  if (!models) return <Loading />;
  if (models.length === 0) {
    return <EmptyState title="Catalog is empty" subtitle="No models added yet. Check back later." />;
  }

  return (
    <div style={{ padding: '0 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {models.map((m) => (
          <ModelCard key={m.id} model={m} />
        ))}
      </div>
    </div>
  );
}

function ModelCard({ model }: { model: Model }) {
  // Hash the name for a stable hue
  const hue = [...model.name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  const initials = model.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        aspectRatio: '0.85',
        borderRadius: 18,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        background: `linear-gradient(160deg, oklch(78% 0.12 ${hue}) 0%, oklch(58% 0.16 ${(hue + 30) % 360}) 100%)`,
        display: 'flex',
        alignItems: 'flex-end',
        padding: 10,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 10,
          fontFamily: 'ui-monospace, monospace',
          fontSize: 9,
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        {initials}
      </div>
      <div
        style={{
          fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: '#fff',
          textShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}
      >
        {model.name}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function MyKissesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Generation[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listGenerations({ limit: 20 })
      .then((r) => setItems(r.items))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <EmptyState title="Couldn't load history" subtitle={error} />;
  if (!items) return <Loading />;
  if (items.length === 0) {
    return (
      <EmptyState
        title="No kisses yet"
        subtitle="Generate your first one and it'll show up here."
        ctaLabel="Create your first"
        onCta={() => navigate('/')}
      />
    );
  }

  return (
    <div style={{ padding: '0 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {items.map((g) => (
          <KissTile
            key={g.id}
            gen={g}
            onTap={() => {
              if (g.status === 'done' && g.output_video_url) {
                // Terminal success — go straight to /result. Skip /generating
                // (would just immediately re-route here after one extra poll).
                navigate('/result', {
                  state: {
                    videoUrl: g.output_video_url,
                    kissType: g.kiss_type,
                    durationSec: g.duration_sec,
                    // Pass scene slug so ResultPage shows the right gradient
                    // + scene name even when entering from Library (not just
                    // from the generation flow).
                    scene: g.scene,
                  },
                });
                return;
              }
              if (
                g.status === 'pending' ||
                g.status === 'processing' ||
                g.status === 'failed'
              ) {
                // In-progress AND failed both route to /generating — its
                // derivePhase() handles the full state machine:
                //   - pending / processing → 2-phase SpinnerTrack with
                //     orbital bubbles / pair thumb / success transition
                //   - failed → refund banner + categorized title/hint +
                //     "Try again with different photos" CTA
                // Orbital bubbles fall back to gradient since photo1/photo2
                // are empty when entering from Library (no wizard flow).
                navigate('/generating', { state: { id: g.id } });
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}

function KissTile({ gen, onTap }: { gen: Generation; onTap: () => void }) {
  const date = new Date(gen.created_at);
  const dateLabel = formatDateLabel(date);
  const isPlayable = gen.status === 'done' && !!gen.output_video_url;
  const isInProgress = gen.status === 'pending' || gen.status === 'processing';
  const isFailed = gen.status === 'failed';
  // All three states drive different views — none is a dead tap anymore.
  const isTappable = isPlayable || isInProgress || isFailed;

  return (
    <button
      onClick={onTap}
      disabled={!isTappable}
      style={{
        appearance: 'none',
        border: 'none',
        padding: 0,
        cursor: isTappable ? 'pointer' : 'default',
        aspectRatio: '1',
        borderRadius: 18,
        overflow: 'hidden',
        position: 'relative',
        // Failed tiles get a destructive tint so user can read state at a
        // glance without parsing the centered label. In-progress + done
        // keep the brand warm gradient.
        background: isFailed
          ? 'linear-gradient(180deg, #FF9A6C 0%, #C44569 40%, rgba(196, 69, 105, 0.4) 100%)'
          : 'linear-gradient(180deg, #FF9A6C 0%, #C44569 50%, #3D2347 100%)',
        // Done = full opacity. In-progress slightly dimmed (signals waiting).
        // Failed slightly dimmed too (signals terminal not-success).
        opacity: isPlayable ? 1 : 0.7,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          padding: '3px 8px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(8px)',
          fontSize: 10,
          color: '#fff',
          fontWeight: 500,
          fontFamily,
        }}
      >
        {dateLabel}
      </div>
      {isPlayable ? (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 36,
            height: 36,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="#1A1117">
            <path d="M7 4l13 8-13 8V4z" />
          </svg>
        </div>
      ) : isInProgress ? (
        // Pulsing dot + "Generating…" — communicates "tap to see progress".
        // We deliberately avoid putting the literal "processing" / "pending"
        // status string here — those are technical states, not user copy.
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'dk-tile-pulse 1.4s ease-in-out infinite',
            }}
          >
            {/* hourglass-ish: simple svg sparkles to match brand */}
            <svg width={16} height={16} viewBox="0 0 24 24" fill="#C44569">
              <path d="M12 2l1.5 4.5h4.5l-3.6 2.7 1.3 4.4-3.7-2.7L8.3 13.6l1.3-4.4L6 6.5h4.5L12 2z" />
            </svg>
          </div>
          <div style={{ color: '#fff', fontSize: 11, fontWeight: 600, fontFamily }}>
            Generating…
          </div>
          <style>{`
            @keyframes dk-tile-pulse {
              0%, 100% { transform: scale(1); }
              50%      { transform: scale(1.15); }
            }
          `}</style>
        </div>
      ) : (
        // Failed — neutral "couldn't finish" label. Detailed reason lives
        // on /generating which is what tap opens. We don't surface the raw
        // error_message here (it'd leak operator-grade traces and crowd
        // the tile).
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            padding: '0 12px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </div>
          <div style={{ color: '#fff', fontSize: 11, fontWeight: 600, fontFamily }}>
            Didn't go through
          </div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 9, fontFamily }}>
            Tap for details
          </div>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          right: 8,
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          fontFamily,
          textShadow: '0 1px 4px rgba(0,0,0,0.3)',
          textAlign: 'left',
        }}
      >
        {gen.kiss_type} · {gen.duration_sec}s
      </div>
    </button>
  );
}

function EmptyState({
  title,
  subtitle,
  ctaLabel,
  onCta,
}: {
  title: string;
  subtitle?: string;
  ctaLabel?: string;
  onCta?: () => void;
}) {
  return (
    <div
      style={{
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color: tg.text, fontFamily }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 13, color: tg.textHint, fontFamily, maxWidth: 280, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      )}
      {ctaLabel && onCta && (
        <button
          onClick={onCta}
          style={{
            marginTop: 8,
            border: 'none',
            background: brand.gradient,
            color: '#fff',
            padding: '10px 22px',
            borderRadius: 12,
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily,
          }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

function Loading() {
  return (
    <div style={{ padding: '48px', display: 'flex', justifyContent: 'center' }}>
      <Spinner size="m" />
    </div>
  );
}

function formatDateLabel(d: Date): string {
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString();
}
